import React, { useContext } from 'react';
import uuid from 'uuid/v4';

import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';

import { useAccessLinkEditMode } from '@confluence/external-share-context/entry-points/AccessLinkEditMode.ts';
import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext.ts';
import { useIsAccessLinkRequest } from '@confluence/external-share-utils/entry-points/useIsAccessLinkRequest.ts';
import { useSwitchToLivePage } from '@confluence/live-pages-features/entry-points/useSwitchToLivePage.ts';
import type { UseSwitchToLivePageProps } from '@confluence/live-pages-features/entry-points/useSwitchToLivePage.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { ACCESS_LINK, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { FORCE_TRANSITION_QUERY_PARAM } from '@confluence/named-routes/entry-points/force-transition.ts';

import { useEditorObjectHeaderContext } from '../../EditorObjectHeaderContext.tsx';

export const ConvertToLivePageItem = ({
	item,
	contentMode,
	spaceId,
	flags,
	spaceKey,
	contentId,
	closeParentMenu,
}: {
	item: any;
	contentMode: any;
	spaceId: any;
	flags: any;
	spaceKey: any;
	contentId: any;
	closeParentMenu: any;
}): React.JSX.Element => {
	const { retrieveEditorTitleAndContent, saveDraft } = useEditorObjectHeaderContext();
	const { push } = useRouteActions();
	const isAccessLinkRequest = useIsAccessLinkRequest();
	const { shareToken } = useContext(ExternalShareContext);
	const { exitEditMode } = useAccessLinkEditMode();

	// An Access Link viewer has no space permission, so the normal page route shows them a "space
	// locked" screen. Stay on the Access Link route; the force-transition param makes the router
	// re-render the same route with the converted content instead of reusing the SSR data.
	const navigateAfterConversion = (forceTransition: boolean) => {
		if (isAccessLinkRequest && shareToken) {
			// Edit mode on this route is state, not a URL, and nothing clears it on conversion. Without this
			// the in-place editor stays mounted over what is now a live doc, which the view page renders.
			exitEditMode();
			push(
				ACCESS_LINK.toUrl({ shareToken }, { query: { [FORCE_TRANSITION_QUERY_PARAM]: uuid() } }),
			);
			return;
		}

		push(
			VIEW_PAGE.toUrl(
				{
					spaceKey,
					contentId,
				},
				// do not remove this query param. It is used to force a transition since the route is the same
				// and we need to force a transition to avoid using SSR data.
				forceTransition ? { query: { [FORCE_TRANSITION_QUERY_PARAM]: uuid() } } : undefined,
			),
		);
	};

	const useSwitchToLivePagesProps =
		contentMode === 'view'
			? {
					contentId,
					spaceId,
					isUnpublishedDraft: false,
					flags,
					analyticsSource: 'viewPageScreen',
					adfOrigin: 'RENDERER',
					onSuccessfulConversion: () => navigateAfterConversion(true),
					retrieveEditorTitleAndContent: undefined,
				}
			: {
					contentId,
					spaceId,
					isUnpublishedDraft: contentMode === 'draft',
					flags,
					retrieveEditorTitleAndContent,
					saveDraft,
					analyticsSource: 'ellipsisDropdownComponent',
					adfOrigin: 'EDITOR',
					// Redirect to the route for the new live page.
					onSuccessfulConversion: () => navigateAfterConversion(false),
				};

	const switchToLivePage = useSwitchToLivePage(
		useSwitchToLivePagesProps as UseSwitchToLivePageProps,
	);

	return (
		<DropdownItem
			key={item.id}
			onClick={() => {
				closeParentMenu();
				void switchToLivePage();
			}}
			elemBefore={<PageLiveDocIcon label="" />}
		>
			{item.label}
		</DropdownItem>
	);
};
